import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useNavigate } from "react-router";
import CustomButton from "src/components/CustomButton";
import CustomTextField from "src/components/CustomTextfield";
import CustomTypography from "src/components/CustomTypography";
import AuthHeader from "./AuthHeader";
import styles from "./SignIn.module.scss";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { SET_VERIFY_OTP } from "src/store/Action/AuthActions/SetUserAction";
import { toast } from "react-toastify";
import { ROUTES } from "src/constant";
import CircularProgress from "@mui/material/CircularProgress";
import FormHelperText from "@mui/material/FormHelperText";
import {
  getToken,
  navigateBasedOnPermissions,
  removeRole,
  removeToken,
  setToken,
} from "src/components/Common/Utils";
import Cookie from "js-cookie";
import { useMutation } from "@apollo/client";
import { SEND_OTP, VERIFY_OTP } from "src/graphql/mutations";
import CryptoJS from "crypto-js";
import { handleGtmClick } from "src/utils/useGTM";

export const ENCRYPTED_SECRET_KEY = "X&O`Y?q43hjQ{v9@";

const SignIn = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [userName, setUserName] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState<Boolean>(false);
  const [timer, setTimer] = useState(60);
  const inputRefs = useRef<any>([]);
  const [verifyOtp, { loading: verifying }] = useMutation(VERIFY_OTP);
  const [sendOtp, { loading: sending }] = useMutation(SEND_OTP);
  const [isBlocked, setIsBlocked] = useState<boolean>(false);

  const theme = createTheme({
    palette: {
      primary: {
        main: "#1976d2",
      },
    },
    typography: {
      fontFamily: "Roboto, Arial, sans-serif",
    },
    components: {
      MuiContainer: {
        styleOverrides: {
          root: {
            paddingTop: "40px",
            paddingBottom: "40px",
          },
        },
      },
    },
  });

  const getIpAddress = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.error('Error fetching IP address:', error);
      return null;
    }
  };

  useEffect(() => {
    let countdown: any;
    if (otpSent && timer > 0) {
      countdown = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(countdown);
  }, [otpSent, timer]);

  const handleSendOtpClick = async () => {
    setLoading(true);
    setError(null);
    const isResend = otpSent;

    try {
      const ipAddress = await getIpAddress();
      handleGtmClick(isResend ? "resendotp_click" : "sendotp_click", {
        page_source: isResend ? "ResendOtp" : "SendOtp",
        page_component: isResend ? "resend_otp" : "send_otp",
        ip_address: ipAddress,
      });
      const res = await sendOtp({
        variables: {
          userName,
          ipAddress
        }
      });

      if (res?.data?.sendOTP?.status === 200) {
        toast.success(res?.data?.sendOTP?.message);
        setOtpSent(true);
        setTimer(60);
      } else if (res?.data?.sendOTP?.status === 403) {
        setIsBlocked(true);
        toast.error("Your account has been blocked. Please contact support.");
        return;
      } else {
        throw new Error(res?.data?.sendOTP?.message);
      }
    } catch (error: any) {
      toast.error(error?.message || "Error sending OTP");
      setError(error?.message || "An error occurred during sign-in.");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpKeyDown = (index: any, e: any) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleVerifyOtp = async () => {
    handleGtmClick("verify_click", {
      page_source: "VerifyOtp",
      page_component: "verify_otp",
      label: "verify_otp",
    });
    try {
      const ipAddress = await getIpAddress();
      const res = await dispatch(SET_VERIFY_OTP({
        userName,
        otp,
        ipAddress
      }));

      if (res?.verifyOTP?.statusCode === 200) {
        const accessToken = res?.verifyOTP?.accessToken;
        const refreshToken = res?.verifyOTP?.refreshToken;

        setToken(accessToken);

        Cookie.set("role", res?.verifyOTP?.user?.role, {
          secure: true,
          sameSite: "Strict",
          expires: 1,
        });

        Cookie.set("accessToken", accessToken, {
          secure: true,
          sameSite: "Strict",
          expires: 1,
        });

        Cookie.set("refreshToken", refreshToken, {
          secure: true,
          sameSite: "Strict",
          expires: 1,
        });

        const encryptedPermissions = CryptoJS.AES.encrypt(
          JSON.stringify(res?.verifyOTP?.privacy?.permissions),
          ENCRYPTED_SECRET_KEY
        ).toString();

        localStorage.setItem("permissions", encryptedPermissions);

        Cookie.set("isEnvAdmin", res?.verifyOTP?.isEnvAdmin, {
          secure: true,
          sameSite: "Strict",
          expires: 1,
        });

        toast.success(res?.verifyOTP?.message);
        navigateBasedOnPermissions(navigate, 1000);
      } else {
        toast.error(res?.verifyOTP?.message);
      }
    } catch (error: any) {
      toast.error(error?.message || "Error verifying OTP");
      if (error?.message === "Too many failed attempts. Your account has been temporarily blocked.") {
        setIsBlocked(true);
        console.error("Your account has been blocked. Please contact support.");
        return;
      }
    }
  };

  const getRoute: any = localStorage.getItem("permissionRoute");

  useEffect(() => {
    const token = getToken();
    if (token) {
      navigate(getRoute);
    }
  }, [navigate]);

  return (
    <ThemeProvider theme={theme}>
      <Container component="main" maxWidth="sm">
        <CssBaseline />
        <Box className={styles.formContainer}>
          <AuthHeader headerText="Welcome To Hi Surat" />
          {isBlocked ? (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <CustomTypography variant="h6" color="error">
                Your Account has been Blocked.
              </CustomTypography>
              <div style={{ textAlign: "center", marginTop: "10px" }}>
              </div>
              <CustomTypography variant="body1">
                Please contact support for Assistance.
              </CustomTypography>
            </Box>
          ) : (
            <Box component="form" noValidate sx={{ mt: 1 }}>
              {!otpSent ? (
                <div className={styles.textfield}>
                  <CustomTextField
                    label="Username"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    variant="outlined"
                    placeholder="Enter your username"
                    fullWidth
                  />
                  {error && (
                    <FormHelperText error style={{ marginTop: "10px" }}>
                      {error}
                    </FormHelperText>
                  )}
                  <CustomButton
                    className={styles.signInBtnWrapper}
                    onClick={handleSendOtpClick}
                    disabled={sending || !userName}
                    fullWidth
                  >
                    <CustomTypography variant="body2" className={styles.btnTextWrapper}>
                      {sending ? <CircularProgress size={20} color="inherit" /> : "Sign In With OTP"}
                    </CustomTypography>
                  </CustomButton>
                </div>
              ) : (
                <>
                  <div className={styles.textfield}
                    style={{ display: "flex", flexDirection: "row", gap: "10px", justifyContent: "center" }}
                  >
                    <CustomTextField
                      label="OTP"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      variant="outlined"
                      placeholder="Enter your OTP"
                      fullWidth
                    />
                  </div>
                  <div style={{ textAlign: "center", marginTop: "10px" }}>
                  </div>
                  <CustomButton
                    className={styles.signInBtnWrapper}
                    onClick={timer > 0 ? handleVerifyOtp : handleSendOtpClick}
                    disabled={(timer > 0 && (otp.length !== 8 || verifying)) || (timer === 0 && sending)}
                    fullWidth
                  >
                    <CustomTypography
                      variant="body2"
                      className={styles.btnTextWrapper}
                      style={{
                        opacity: (timer > 0 && (otp.length !== 8 || verifying)) || (timer === 0 && sending) ? 0.5 : 1,
                        pointerEvents: "none",
                      }}
                    >
                      {timer > 0 ? (
                        verifying ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          "Verify OTP"
                        )
                      ) : (
                        sending ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          "Resend OTP"
                        )
                      )}
                    </CustomTypography>
                  </CustomButton>
                  {timer > 0 ? (
                    <CustomTypography variant="smallText" color="primary">OTP expires in {Math.floor(timer / 60)}:{(timer % 60).toString().padStart(2, "0")}</CustomTypography>
                  ) : (
                    <CustomTypography variant="smallText" color="error">OTP expired. Please resend OTP.</CustomTypography>
                  )}
                </>
              )}
            </Box>
          )}
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default SignIn;